import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import {
  X, ArrowRight, ArrowLeft, Check, Loader2, Plus, Trash2,
  BookOpen, FlaskConical, Shield, GraduationCap, Globe,
  ChevronDown, ChevronUp, Pencil, Eye, FileText, Users,
  Calendar, FolderKanban, Sparkles, CheckCircle2,
} from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

const ICON_MAP = {
  BookOpen, FlaskConical, Shield, GraduationCap, Globe,
};

/* ─── Step Indicator ─── */
const StepIndicator = ({ steps, current }) => (
  <div className="flex items-center gap-1 px-6 py-3 border-b border-border/40" data-testid="wizard-steps">
    {steps?.map((s, i) => (
      <div key={i} className="flex items-center gap-1 flex-1">
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
          i === current ? "bg-[#014d7d]/8 text-[#014d7d]" :
          i < current ? "bg-emerald-50 text-emerald-600" : "text-foreground/40"
        }`} data-testid={`step-${i}`}>
          {i < current ? <CheckCircle2 className="w-3.5 h-3.5" /> :
           <span className="w-5 h-5 rounded-full border-2 flex items-center justify-center text-[10px] font-bold" style={{ borderColor: i === current ? "#014d7d" : "currentColor" }}>{i + 1}</span>}
          <span className="hidden sm:inline">{s}</span>
        </div>
        {i < steps.length - 1 && <div className={`flex-1 h-px mx-1 ${i < current ? "bg-emerald-300" : "bg-border/40"}`} />}
      </div>
    ))}
  </div>
);

/* ─── Template Preview/Editor ─── */
const TemplateEditor = ({ template, onSave, onClose }) => {
  const [content, setContent] = useState(template.template);
  const [mode, setMode] = useState("preview");

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/30 backdrop-blur-sm" data-testid="template-editor-modal">
      <div className="bg-white rounded-2xl border border-border/60 w-full max-w-3xl max-h-[85vh] flex flex-col shadow-elevated">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40">
          <div>
            <h3 className="font-heading font-semibold text-sm text-foreground">{template.type}</h3>
            <p className="text-[11px] text-foreground/50">{template.stage} Stage — {template.brief}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button onClick={() => setMode("preview")} className={`px-3 py-1 rounded-md text-xs font-medium ${mode === "preview" ? "bg-white shadow-sm text-foreground" : "text-foreground/50"}`} data-testid="template-preview-tab">
                <Eye className="w-3 h-3 inline mr-1" />Preview
              </button>
              <button onClick={() => setMode("edit")} className={`px-3 py-1 rounded-md text-xs font-medium ${mode === "edit" ? "bg-white shadow-sm text-foreground" : "text-foreground/50"}`} data-testid="template-edit-tab">
                <Pencil className="w-3 h-3 inline mr-1" />Edit
              </button>
            </div>
            <button onClick={onClose} className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center" data-testid="close-template-editor">
              <X className="w-4 h-4 text-foreground/50" />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-5 min-h-0">
          {mode === "preview" ? (
            <div className="prose prose-sm max-w-none" data-testid="template-preview-content">
              <ReactMarkdown
                components={{
                  h2: ({ children }) => <h2 className="text-lg font-heading font-bold text-foreground mt-0 mb-3 pb-2 border-b border-border/40">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-sm font-heading font-semibold text-foreground mt-4 mb-2">{children}</h3>,
                  h4: ({ children }) => <h4 className="text-xs font-heading font-semibold text-foreground/80 mt-3 mb-1">{children}</h4>,
                  p: ({ children }) => <p className="text-sm text-foreground/70 mb-2 leading-relaxed">{children}</p>,
                  table: ({ children }) => <div className="overflow-x-auto mb-3"><table className="w-full text-xs border-collapse">{children}</table></div>,
                  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
                  th: ({ children }) => <th className="px-3 py-2 text-left font-semibold text-foreground/70 border border-border/40">{children}</th>,
                  td: ({ children }) => <td className="px-3 py-2 text-foreground/60 border border-border/40">{children}</td>,
                  ul: ({ children }) => <ul className="list-disc pl-5 mb-2 space-y-0.5 text-sm text-foreground/70">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5 text-sm text-foreground/70">{children}</ol>,
                  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
                  strong: ({ children }) => <strong className="font-semibold text-foreground/80">{children}</strong>,
                  code: ({ children }) => <code className="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono text-[#014d7d]">{children}</code>,
                  pre: ({ children }) => <pre className="bg-gray-50 rounded-lg p-3 mb-3 overflow-x-auto text-xs">{children}</pre>,
                  hr: () => <hr className="my-4 border-border/40" />,
                }}
              >{content}</ReactMarkdown>
            </div>
          ) : (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              className="w-full h-full min-h-[400px] p-4 font-mono text-sm text-foreground border border-border/60 rounded-xl resize-none outline-none focus:ring-2 focus:ring-[#014d7d]/10 focus:border-[#014d7d]/30 leading-relaxed bg-gray-50/50"
              data-testid="template-edit-textarea"
            />
          )}
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-border/40">
          <p className="text-[10px] text-foreground/40">Markdown formatting supported</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-xs font-medium rounded-lg border border-border/60 text-foreground/60 hover:bg-gray-50" data-testid="template-cancel-btn">Cancel</button>
            <button onClick={() => { onSave(content); onClose(); }} className="px-4 py-2 text-xs font-semibold rounded-lg bg-[#014d7d] text-white hover:bg-[#013d66]" data-testid="template-save-btn">Save Changes</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════ */
export const CreateProjectWizard = ({ isOpen, onClose, prefillGoal, prefillWorkflowType }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Step 1: Project details
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");

  // Step 2: Workflow selection
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [expandedStage, setExpandedStage] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [localTemplates, setLocalTemplates] = useState({});

  // Step 3: Team
  const [teamMembers, setTeamMembers] = useState([]);
  const [newMemberName, setNewMemberName] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("member");
  const [newMemberEmail, setNewMemberEmail] = useState("");

  const nameRef = useRef(null);

  // Prefill from props
  useEffect(() => {
    if (isOpen) {
      if (prefillGoal) {
        setName(prefillGoal.length > 60 ? prefillGoal.slice(0, 57) + "..." : prefillGoal);
        setDescription(prefillGoal);
      }
      setStep(0);
    }
  }, [isOpen, prefillGoal]);

  // Fetch workflows
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/workflows`, { withCredentials: true });
        const arr = Array.isArray(data) ? data : [];
        setWorkflows(arr);
        // Auto-select based on prefill type
        if (prefillWorkflowType && arr.length > 0) {
          const match = arr.find(w => w.name.toLowerCase().includes(prefillWorkflowType.toLowerCase()));
          if (match) setSelectedWorkflow(match);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [isOpen, prefillWorkflowType]);

  const handleAddMember = () => {
    if (!newMemberName.trim()) return;
    const colors = ["#1a6fc4", "#178a55", "#6941c6", "#c8841a", "#b84040", "#0e7490"];
    setTeamMembers(prev => [...prev, {
      name: newMemberName.trim(),
      role: newMemberRole,
      email: newMemberEmail.trim(),
      color: colors[prev.length % colors.length],
    }]);
    setNewMemberName("");
    setNewMemberEmail("");
    setNewMemberRole("member");
  };

  const handleRemoveMember = (idx) => setTeamMembers(prev => prev?.filter((_, i) => i !== idx));

  const handleSaveTemplate = useCallback(async (content) => {
    if (!editingTemplate || !selectedWorkflow) return;
    const key = `${editingTemplate.stage}::${editingTemplate.type}`;
    setLocalTemplates(prev => ({ ...prev, [key]: content }));
    // Persist to backend
    try {
      await axios.put(`${API}/workflows/${selectedWorkflow.id}/templates`, {
        templates: [{ stage: editingTemplate.stage, type: editingTemplate.type, template: content }],
      }, { withCredentials: true });
    } catch (e) { console.error(e); }
  }, [editingTemplate, selectedWorkflow]);

  const getTemplateContent = (stage, type, original) => {
    const key = `${stage}::${type}`;
    return localTemplates[key] ?? original;
  };

  const handleCreate = async () => {
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const { data: project } = await axios.post(`${API}/projects`, {
        name: name.trim(),
        description: description.trim(),
        workflow_id: selectedWorkflow?.id || null,
        due_date: dueDate || null,
        team_members: teamMembers.length > 0 ? teamMembers : null,
      }, { withCredentials: true });

      // Create initial session with the goal
      const goal = description.trim() || name.trim();
      const { data: session } = await axios.post(`${API}/projects/${project.id}/sessions`, {
        goal, expertise_level: "intermediate",
      }, { withCredentials: true });

      onClose();
      navigate(`/projects/${project.id}`, { state: { autoSendGoal: goal, sessionId: session.session.id } });
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  if (!isOpen) return null;

  const STEPS = ["Project Details", "Workflow", "Team", "Review"];
  const canNext = step === 0 ? name.trim().length > 0 : step === 1 ? true : true;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25 backdrop-blur-sm" data-testid="create-project-wizard">
      <div className="bg-white rounded-2xl border border-border/60 w-full max-w-2xl max-h-[88vh] flex flex-col shadow-elevated" style={{ animation: "cardFadeIn 0.2s ease-out" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border/40">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#014d7d]/8 flex items-center justify-center">
              <FolderKanban className="w-4.5 h-4.5 text-[#014d7d]" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-base text-foreground">Create Project</h2>
              <p className="text-[11px] text-foreground/50">Set up your project with workflow and team</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center" data-testid="close-wizard">
            <X className="w-4 h-4 text-foreground/50" />
          </button>
        </div>

        <StepIndicator steps={STEPS} current={step} />

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-foreground/45" /></div>
          ) : step === 0 ? (
            /* ─── Step 1: Project Details ─── */
            <div className="space-y-4" data-testid="step-details">
              <div>
                <label className="text-xs font-semibold text-foreground/70 mb-1.5 block">Project Name *</label>
                <input
                  ref={nameRef} value={name} onChange={(e) => setName(e.target.value)} autoFocus
                  placeholder="e.g., Biology 101 Course Development"
                  className="w-full h-10 px-3.5 text-sm border border-border/60 rounded-xl outline-none focus:ring-2 focus:ring-[#014d7d]/10 focus:border-[#014d7d]/30"
                  data-testid="project-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-foreground/70 mb-1.5 block">Description</label>
                <textarea
                  value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what this project will accomplish..."
                  className="w-full px-3.5 py-2.5 text-sm border border-border/60 rounded-xl outline-none focus:ring-2 focus:ring-[#014d7d]/10 focus:border-[#014d7d]/30 resize-none leading-relaxed"
                  rows={3}
                  data-testid="project-desc-input"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-foreground/70 mb-1.5 block">Due Date (optional)</label>
                <input
                  type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                  className="w-full h-10 px-3.5 text-sm border border-border/60 rounded-xl outline-none focus:ring-2 focus:ring-[#014d7d]/10 focus:border-[#014d7d]/30"
                  data-testid="project-due-input"
                />
              </div>
            </div>
          ) : step === 1 ? (
            /* ─── Step 2: Workflow Selection + Template Preview ─── */
            <div className="space-y-4" data-testid="step-workflow">
              <p className="text-xs text-foreground/50 mb-1">Select a workflow pipeline to structure your project. Each workflow defines stages and artifact templates.</p>
              <div className="grid gap-3">
                {workflows?.map((wf) => {
                  const Icon = ICON_MAP[wf.icon] || FolderKanban;
                  const isSelected = selectedWorkflow?.id === wf.id;
                  const stages = wf.stages || [];
                  return (
                    <div key={wf.id}>
                      <button
                        onClick={() => { setSelectedWorkflow(isSelected ? null : wf); setExpandedStage(null); }}
                        className={`w-full text-left rounded-xl border-2 p-4 transition-all ${
                          isSelected ? "border-[#014d7d] bg-[#014d7d]/[0.03]" : "border-border/60 hover:border-border"
                        }`}
                        data-testid={`workflow-card-${wf.id}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${wf.color || "#014d7d"}14` }}>
                            <Icon className="w-5 h-5" style={{ color: wf.color || "#014d7d" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <h4 className="text-sm font-semibold text-foreground">{wf.name}</h4>
                              {isSelected && <CheckCircle2 className="w-4 h-4 text-[#014d7d] shrink-0" />}
                            </div>
                            <p className="text-xs text-foreground/50 mt-0.5 leading-relaxed">{wf.description}</p>
                            <div className="flex items-center gap-1.5 mt-2.5 flex-wrap">
                              {stages?.map((s, i) => (
                                <span key={i} className="flex items-center gap-1">
                                  <span className="text-[10px] font-semibold px-2 py-0.5 rounded-md bg-gray-100 text-foreground/60">{s.name}</span>
                                  {i < stages.length - 1 && <ArrowRight className="w-2.5 h-2.5 text-foreground/30" />}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </button>

                      {/* Template preview for selected workflow */}
                      {isSelected && (
                        <div className="mt-2 ml-1 border-l-2 border-[#014d7d]/20 pl-4 space-y-1.5" data-testid="workflow-template-preview">
                          <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-2">Artifact Templates ({stages.reduce((sum, s) => sum + (s.artifacts?.length || 0), 0)})</p>
                          {stages?.map((stage, si) => (
                            <div key={si}>
                              <button
                                onClick={() => setExpandedStage(expandedStage === si ? null : si)}
                                className="w-full flex items-center justify-between py-1.5 text-left"
                                data-testid={`stage-toggle-${si}`}
                              >
                                <span className="text-xs font-semibold text-foreground/70">{stage.name} <span className="text-foreground/40 font-normal">({(stage.artifacts || []).length} templates)</span></span>
                                {expandedStage === si ? <ChevronUp className="w-3.5 h-3.5 text-foreground/40" /> : <ChevronDown className="w-3.5 h-3.5 text-foreground/40" />}
                              </button>
                              {expandedStage === si && (
                                <div className="space-y-1 mb-2">
                                  {(stage.artifacts || [])?.map((art, ai) => (
                                    <div key={ai} className="flex items-center justify-between pl-2 py-1.5 rounded-lg hover:bg-gray-50 group">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <FileText className="w-3.5 h-3.5 text-foreground/40 shrink-0" />
                                        <div className="min-w-0">
                                          <p className="text-xs font-medium text-foreground/70 truncate">{art.type}</p>
                                          <p className="text-[10px] text-foreground/40 truncate">{art.brief}</p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 ml-2">
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setEditingTemplate({ ...art, stage: stage.name, template: getTemplateContent(stage.name, art.type, art.template) }); }}
                                          className="px-2 py-1 rounded-md text-[10px] font-medium text-[#014d7d] hover:bg-[#014d7d]/5 flex items-center gap-1"
                                          data-testid={`preview-template-${si}-${ai}`}
                                        >
                                          <Eye className="w-3 h-3" /> Preview
                                        </button>
                                        <button
                                          onClick={(e) => { e.stopPropagation(); setEditingTemplate({ ...art, stage: stage.name, template: getTemplateContent(stage.name, art.type, art.template) }); }}
                                          className="px-2 py-1 rounded-md text-[10px] font-medium text-foreground/50 hover:bg-gray-100 flex items-center gap-1"
                                          data-testid={`edit-template-${si}-${ai}`}
                                        >
                                          <Pencil className="w-3 h-3" /> Edit
                                        </button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {!selectedWorkflow && (
                <p className="text-xs text-foreground/40 italic text-center py-2">You can skip workflow selection and assign one later.</p>
              )}
            </div>
          ) : step === 2 ? (
            /* ─── Step 3: Team Members ─── */
            <div className="space-y-4" data-testid="step-team">
              <p className="text-xs text-foreground/50">Add team members who will collaborate on this project.</p>
              {/* Add member form */}
              <div className="p-4 bg-gray-50 rounded-xl border border-border/40">
                <div className="grid grid-cols-3 gap-2 mb-2">
                  <input
                    value={newMemberName} onChange={(e) => setNewMemberName(e.target.value)}
                    placeholder="Name" className="h-9 px-3 text-xs border border-border/60 rounded-lg outline-none focus:ring-2 focus:ring-[#014d7d]/10"
                    data-testid="team-name-input"
                  />
                  <input
                    value={newMemberEmail} onChange={(e) => setNewMemberEmail(e.target.value)}
                    placeholder="Email (optional)" className="h-9 px-3 text-xs border border-border/60 rounded-lg outline-none focus:ring-2 focus:ring-[#014d7d]/10"
                    data-testid="team-email-input"
                  />
                  <select
                    value={newMemberRole} onChange={(e) => setNewMemberRole(e.target.value)}
                    className="h-9 px-3 text-xs border border-border/60 rounded-lg outline-none focus:ring-2 focus:ring-[#014d7d]/10 bg-white"
                    data-testid="team-role-select"
                  >
                    <option value="member">Member</option>
                    <option value="instructional_designer">Instructional Designer</option>
                    <option value="content_editor">Content Editor</option>
                    <option value="sme">Subject Matter Expert</option>
                    <option value="reviewer">Reviewer</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <button
                  onClick={handleAddMember} disabled={!newMemberName.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-[#014d7d] text-white disabled:opacity-30 hover:bg-[#013d66]"
                  data-testid="add-member-btn"
                >
                  <Plus className="w-3 h-3" /> Add Member
                </button>
              </div>

              {/* Members list */}
              {teamMembers.length > 0 ? (
                <div className="space-y-2" data-testid="team-members-list">
                  {teamMembers?.map((m, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3 bg-white border border-border/40 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: m.color }}>
                          {m.name.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground">{m.name}</p>
                          <p className="text-[10px] text-foreground/50 capitalize">{m.role.replace("_", " ")}{m.email ? ` · ${m.email}` : ""}</p>
                        </div>
                      </div>
                      <button onClick={() => handleRemoveMember(i)} className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center" data-testid={`remove-member-${i}`}>
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-8 h-8 text-foreground/10 mx-auto mb-2" />
                  <p className="text-xs text-foreground/40">No team members added yet. You can add them later.</p>
                </div>
              )}
            </div>
          ) : (
            /* ─── Step 4: Review ─── */
            <div className="space-y-4" data-testid="step-review">
              <div className="bg-gray-50 rounded-xl border border-border/40 p-5 space-y-4">
                <div>
                  <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1">Project</p>
                  <p className="text-sm font-semibold text-foreground">{name}</p>
                  {description && <p className="text-xs text-foreground/50 mt-0.5">{description}</p>}
                  {dueDate && <p className="text-xs text-foreground/50 mt-0.5 flex items-center gap-1"><Calendar className="w-3 h-3" /> Due: {new Date(dueDate).toLocaleDateString()}</p>}
                </div>
                <div className="h-px bg-border/40" />
                <div>
                  <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1">Workflow</p>
                  {selectedWorkflow ? (
                    <div className="flex items-center gap-2">
                      {(() => { const Icon = ICON_MAP[selectedWorkflow.icon] || FolderKanban; return <Icon className="w-4 h-4" style={{ color: selectedWorkflow.color }} />; })()}
                      <span className="text-sm font-medium text-foreground">{selectedWorkflow.name}</span>
                      <span className="text-[10px] text-foreground/40">({(selectedWorkflow.stages || []).length} stages)</span>
                    </div>
                  ) : (
                    <p className="text-xs text-foreground/40 italic">No workflow selected</p>
                  )}
                </div>
                <div className="h-px bg-border/40" />
                <div>
                  <p className="text-[10px] font-bold text-foreground/50 uppercase tracking-wider mb-1">Team ({teamMembers.length + 1} members)</p>
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#014d7d]/8 text-[#014d7d]">You (Owner)</span>
                    {teamMembers?.map((m, i) => (
                      <span key={i} className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-foreground/60">{m.name}</span>
                    ))}
                  </div>
                </div>
              </div>
              {selectedWorkflow && (
                <div className="bg-emerald-50/50 border border-emerald-200/50 rounded-xl p-4">
                  <p className="text-xs text-emerald-700 flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" />
                    AI will generate artifacts following the <strong>{selectedWorkflow.name}</strong> templates.
                    {(selectedWorkflow.stages || []).reduce((sum, s) => sum + (s.artifacts?.length || 0), 0)} templates will guide content creation.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3.5 border-t border-border/40">
          <button
            onClick={() => step > 0 ? setStep(step - 1) : onClose()}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg border border-border/60 text-foreground/60 hover:bg-gray-50"
            data-testid="wizard-back-btn"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {step > 0 ? "Back" : "Cancel"}
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={() => setStep(step + 1)} disabled={!canNext}
              className="flex items-center gap-1.5 px-5 py-2 text-xs font-semibold rounded-lg bg-[#014d7d] text-white hover:bg-[#013d66] disabled:opacity-30"
              data-testid="wizard-next-btn"
            >
              Next <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={handleCreate} disabled={creating || !name.trim()}
              className="flex items-center gap-1.5 px-5 py-2 text-xs font-semibold rounded-lg bg-[#014d7d] text-white hover:bg-[#013d66] disabled:opacity-30"
              data-testid="wizard-create-btn"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
              {creating ? "Creating..." : "Create Project"}
            </button>
          )}
        </div>
      </div>

      {/* Template Editor Overlay */}
      {editingTemplate && (
        <TemplateEditor
          template={editingTemplate}
          onSave={handleSaveTemplate}
          onClose={() => setEditingTemplate(null)}
        />
      )}
    </div>
  );
};
